import React from "react";
import { Button, Drawer, Modal } from "antd";
import { DeleteOutlined, EditOutlined, ForkOutlined, PlusOutlined, UndoOutlined } from "@ant-design/icons";
import "./definition-drawer.scss";

/**
 * `branch` creates a second definition with the same name as an existing one
 * ("another way to match") — how an OR between rule groups is expressed.
 */
export type DefinitionFormMode = "create" | "edit" | "delete" | "restore" | "branch";

export const DEFINITION_ACTION_TEXT: Record<DefinitionFormMode, string> = {
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  restore: "Restore",
  branch: "Add another way to match",
};

const buttonIcon: Record<DefinitionFormMode, React.ReactNode> = {
  create: <PlusOutlined />,
  edit: <EditOutlined />,
  delete: <DeleteOutlined />,
  restore: <UndoOutlined />,
  branch: <ForkOutlined />,
};

/** The modes that open a form in a drawer; the others ask for confirmation in a modal. */
export const isDrawerMode = (mode: DefinitionFormMode) => mode === "create" || mode === "edit" || mode === "branch";

export interface DefinitionDrawerProps {
  mode: DefinitionFormMode;
  /** What is being managed, for titles ("Project Type Definition"). */
  noun: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  loading?: boolean;
  disabled?: boolean;
  /** The trigger button's text; defaults from the mode and noun. */
  buttonLabel?: React.ReactNode;
  /** Render the trigger as a small link button (table rows) instead of a primary button. */
  compact?: boolean;
  /** Overrides the drawer / modal title. */
  title?: React.ReactNode;
  /** Drawer: submit the form. */
  onSubmit?: () => void;
  /** Drawer: the submit button's text; defaults to "Save <noun>" / "Create <noun>". */
  submitLabel?: React.ReactNode;
  /** Modal (delete / restore): confirm. */
  onConfirm?: () => void;
  width?: number;
  className?: string;
  /** Drawer: the form. Modal: the confirmation text. */
  children: React.ReactNode;
}

/**
 * The shell every definition form shares: a trigger button, and either a
 * drawer holding the form (create / edit / branch) or a confirmation modal
 * (delete / restore). The feature supplies the form and the submit.
 */
const DefinitionDrawer: React.FC<DefinitionDrawerProps> = ({
  mode,
  noun,
  open,
  onOpen,
  onClose,
  loading = false,
  disabled = false,
  buttonLabel,
  compact = false,
  title,
  onSubmit,
  submitLabel,
  onConfirm,
  width = 520,
  className,
  children,
}) => {
  const label =
    buttonLabel ??
    (mode === "create" ? `Add ${noun}` : mode === "branch" && compact ? "Another way" : DEFINITION_ACTION_TEXT[mode]);
  const heading = title ?? `${DEFINITION_ACTION_TEXT[mode]} ${noun}`;

  return (
    <>
      <Button
        icon={buttonIcon[mode]}
        type={mode === "create" ? "primary" : "link"}
        danger={mode === "delete"}
        size={mode === "create" ? "middle" : "small"}
        onClick={onOpen}
        disabled={disabled}
      >
        {label}
      </Button>

      {isDrawerMode(mode) ? (
        <Drawer
          title={heading}
          open={open}
          onClose={onClose}
          width={width}
          destroyOnClose
          maskClosable={!loading}
          closable={!loading}
          className={`definition-drawer ${className ?? ""}`.trim()}
          footer={
            <div style={{ marginBottom: 120 }}>
              <Button type="primary" loading={loading} onClick={onSubmit} style={{ marginLeft: 8 }}>
                {submitLabel ?? `${mode === "edit" ? "Save" : "Create"} ${noun}`}
              </Button>
            </div>
          }
        >
          {children}
        </Drawer>
      ) : (
        <Modal
          title={heading}
          open={open}
          onCancel={onClose}
          onOk={onConfirm}
          confirmLoading={loading}
          destroyOnClose
          okText={DEFINITION_ACTION_TEXT[mode]}
          okButtonProps={{ danger: mode === "delete" }}
          maskClosable={!loading}
          closable={!loading}
        >
          {children}
        </Modal>
      )}
    </>
  );
};

export default DefinitionDrawer;
